import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  SFFilterIcon,
  SFArrowDownWardIcon,
  SFArrowUpWardIcon,
} from "../../../icons";
import { SFStack } from "../../../grid";
import getStyled from "../../../../hooks/getStyled";
import TooltipButton from "../../tooltipButton";
import SFTypography from "../../../typography";
import _ from "lodash";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(() => ({
  height: "100%",
  width: "100%",
  alignItems: "center",
}));

const SFStackClickable = styled(SFStack)(() => ({
  cursor: "pointer",
}));

const SFTypographyStyled = styled(SFTypography)(() => ({
  fontSize: "11px",
  lineHeight: "15px",
}));

const SFColumnHeaderRenderer = (props) => {
  const [hasFilter, setHasFilter] = useState(false);
  const [ascSort, setAscSort] = useState(false);
  const [descSort, setDescSort] = useState(false);
  const [noSort, setNoSort] = useState(false);

  const onFilterChanged = () => {
    if (props.column.filterActive) {
      setHasFilter(true);
    } else {
      setHasFilter(false);
    }
  };

  const onSortChanged = () => {
    setAscSort(props.column.isSortAscending());
    setDescSort(props.column.isSortDescending());
    setNoSort(
      !props.column.isSortAscending() && !props.column.isSortDescending()
    );
  };

  const onSortRequested = (order, event) => {
    props.setSort(order, event.shiftKey);
  };

  useEffect(() => {
    props.column.addEventListener("filterChanged", onFilterChanged);
    props.column.addEventListener("sortChanged", onSortChanged);

    onFilterChanged();
    onSortChanged();

    return () => {
      props.column.removeEventListener("filterChanged", onFilterChanged);
      props.column.removeEventListener("sortChanged", onSortChanged);
    };
  }, []);

  const onClickHeader = (event) => {
    if (noSort) {
      onSortRequested("desc", event);
    } else if (descSort) {
      onSortRequested("asc", event);
    } else {
      onSortRequested("", event);
    }
  };

  let headerAlignment = "flex-start";
  // Get tooltip properties from either colDef or colDef.context (for AG Grid v33 compatibility)
  const colDef = props.column.colDef;
  const tooltip = colDef.tooltip ?? colDef.context?.tooltip;
  const tooltipWidth = colDef.tooltipWidth ?? colDef.context?.tooltipWidth;
  const tooltipIconColor =
    colDef.tooltipIconColor ?? colDef.context?.tooltipIconColor;
  const tooltipVariant = colDef.tooltipVariant ?? colDef.context?.tooltipVariant;
  const tooltipPosition =
    colDef.tooltipPosition ?? colDef.context?.tooltipPosition;
  const onTooltipClick = colDef.onTooltipClick ?? colDef.context?.onTooltipClick;
  const align = colDef.align ?? colDef.context?.align;

  let textAlign = "left";
  if (align) {
    switch (align) {
    case "left":
    default:
      headerAlignment = "flex-start";
      textAlign = "left";
      break;
    case "right":
      headerAlignment = "flex-end";
      textAlign = "right";
      break;
    case "center":
      headerAlignment = "center";
      textAlign = "center";
      break;
    }
  }

  return (
    <SFStackStyled direction="row">
      <SFStackStyled
        direction="row"
        spacing={1}
        justifyContent={headerAlignment}
      >
        <SFStack direction="row" alignItems={colDef.wrapHeaderText ? "flex-end" : "center"} spacing={0.5}>
          {!_.isNil(tooltip) &&
            (!tooltipPosition || tooltipPosition === "left") && (
            <TooltipButton
              tooltip={tooltip}
              width={tooltipWidth}
              iconColor={tooltipIconColor}
              variant={tooltipVariant}
              onClick={onTooltipClick}
            />
          )}
          <SFStackClickable onClick={onClickHeader}>
            <SFTypographyStyled variant="tableHeader" onClick={onClickHeader} align={textAlign}>
              {props.displayName.toLocaleUpperCase()}
            </SFTypographyStyled>
          </SFStackClickable>
          {!_.isNil(tooltip) && tooltipPosition === "right" && (
            <TooltipButton
              tooltip={tooltip}
              width={tooltipWidth}
              iconColor={tooltipIconColor}
              variant={tooltipVariant}
              onClick={onTooltipClick}
            />
          )}
        </SFStack>
        {hasFilter && <SFFilterIcon size="small" />}
        {ascSort && <SFArrowDownWardIcon size="small" />}
        {descSort && <SFArrowUpWardIcon size="small" />}
      </SFStackStyled>
    </SFStackStyled>
  );
};

SFColumnHeaderRenderer.propTypes = {
  displayName: PropTypes.any,
  column: PropTypes.any,
  enableSorting: PropTypes.any,
  menuIcon: PropTypes.any,
  enableMenu: PropTypes.any,
  setSort: PropTypes.any,
  showColumnMenu: PropTypes.any,
  showFilter: PropTypes.any,
  getMainMenuItems: PropTypes.any,
};

export default SFColumnHeaderRenderer;
